import { ExpensesEntitiesExpensableResourceTypeEnum } from 'generated/resources/graphql'
import { z } from 'zod'

import { BasicDetailsSchema } from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails/form/schema'
import { AdditionalInformationSchema } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/AdditionalInformation/form/schema'
import { BasicInformationSchema } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/BasicInformation/form/schema'
import { BudgetSchema } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Budget/form/schema'
import {
  CategorySchema,
  superRefineCategorySchema,
} from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Category/form/schema'
import { CostCenterSchema } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/CostCenters/form/schema'
import { GroupSchema } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Group/form/schema'
import {
  PaymentInformationSchema,
  superRefinePaymentInformationSchema,
} from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/PaymentInformation/form/schema'
import { ProjectsSchema } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Projects/form/schema'
import {
  InitialTaxSchema,
  superRefineTaxesSchema,
} from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Taxes/form/schema'
import { VendorSchema } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Vendor/form/schema'
import { FilesSchema } from 'modules/expenses/components/expenses/Detail/_newForm/Files/form/schema'
import { FilesPreviewerContextValue } from 'modules/expenses/components/expenses/Detail/hooks/useFilesPreviewer'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'
import { BudgetOption } from 'modules/finance/hooks/useBudgets'

interface FormContext {
  customizationFields: CustomizationField[]
  hasSignedDocuments: boolean
  previewer?: FilesPreviewerContextValue
  budgetOptions: BudgetOption[]
  projectWorkers: ProjectWorker[]
  hasProjectsFeature: boolean
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
}

interface LatestCustomizationFieldsRef {
  current: CustomizationField[]
}
// Create a ref to store the latest customizationFields
// we need this to be able to access it in the super refine function
const customizationFieldsRef: LatestCustomizationFieldsRef = { current: [] }

export const ExpenseSchema = ({
  customizationFields,
  hasSignedDocuments,
  previewer,
  budgetOptions,
  projectWorkers,
  hasProjectsFeature,
  resourceType,
}: FormContext) => {
  customizationFieldsRef.current = customizationFields

  return z
    .object({
      ...BasicDetailsSchema({ customizationFields }).shape,
      ...BasicInformationSchema({ customizationFields }).shape,
      ...CategorySchema().shape,
      ...VendorSchema({ customizationFields }).shape,
      ...AdditionalInformationSchema({ customizationFields, resourceType }).shape,
      ...PaymentInformationSchema({ customizationFields }).shape,
      ...ProjectsSchema({ customizationFields, hasProjectsFeature, projectWorkers, resourceType })
        .shape,
      ...BudgetSchema({ customizationFields, budgetOptions, resourceType }).shape,
      ...FilesSchema({ customizationFields, hasSignedDocuments, previewer, resourceType }).shape,
      ...InitialTaxSchema().shape,
      ...CostCenterSchema({ customizationFields, resourceType }).shape,
      ...GroupSchema().shape,
    })
    .superRefine((data, ctx) => {
      const cfs = customizationFieldsRef.current
      superRefineCategorySchema({
        data,
        ctx,
        customizationFields: cfs,
        resourceType,
      })
      superRefinePaymentInformationSchema({
        data,
        ctx,
        customizationFields: cfs,
      })
      superRefineTaxesSchema({ data, ctx, customizationFields: cfs })
    })
}
export type ExpenseSchema = z.infer<ReturnType<typeof ExpenseSchema>>
